import { Timestamp } from 'firebase/firestore';
import { dev } from '@breath-lab/domain';
import { BLUser, AccessLevel, LoginProvider, loginProviderFromFirebaseId, defaultUserSettings } from '@breath-lab/types';
import {
  getServices,
  type AuthUser,
  type DeleteAccountParams,
  type IUserMapper,
} from '@breath-lab/services';
import { blUserFromFirestore, blUserToFirestore } from '../firebase/userMapper';
import { logEvent } from '../firebase/analytics';
import { AnalyticsEvent, AnalyticsParam } from '../../domain/analytics';
import { captureReferralOnSignup } from '../../domain/sharing';

const WEB_VERSION = import.meta.env.VITE_APP_VERSION as string;
const WEB_BUILD_NUMBER = Number(import.meta.env.VITE_APP_BUILD_NUMBER);

// ---------------------------------------------------------------------------
// Beta access gate — web only. Does not affect the Flutter app.
// All emails must be lowercase. Remove this block when opening to the public.
// DISABLED: gate is currently off; re-enable by uncommenting assertBetaAccess calls below.
// ---------------------------------------------------------------------------
const BETA_ALLOWED_EMAILS = new Set([
  // Add tester emails here, e.g.:
  'evangelospittas@gmail.com',
].map((e) => e.toLowerCase()));

// @ts-expect-error — gate is disabled; re-enable by uncommenting the call sites below
function assertBetaAccess(email: string | null | undefined): void {
  if (!email || !BETA_ALLOWED_EMAILS.has(email.toLowerCase())) {
    const err = new Error('Access is currently restricted to invited testers.');
    (err as Error & { code: string }).code = 'app/beta-access-denied';
    throw err;
  }
}
// ---------------------------------------------------------------------------

export class WebUserMapper implements IUserMapper {
  async signInWithGoogle(): Promise<BLUser | null> {
    try {
      const authUser = await getServices().auth.signInWithGoogle();
      if (!authUser) return null; // redirect initiated — page is navigating away
      // assertBetaAccess(authUser.email);
      const user = await this.registerUserIfNeeded(authUser);
      dev.log('[Auth] Google sign-in successful', { uid: user.uid, email: user.email });
      return user;
    } catch (err) {
      dev.error('[Auth] Google sign-in failed', err);
      if ((err as Error & { code?: string }).code === 'app/beta-access-denied') {
        await getServices().auth.signOut().catch(() => {});
      }
      throw err;
    }
  }

  async handleGoogleRedirectResult(): Promise<BLUser | null> {
    const authUser = await getServices().auth.getGoogleRedirectResult();
    if (!authUser) return null;
    try {
      // assertBetaAccess(authUser.email);
      const user = await this.registerUserIfNeeded(authUser);
      dev.log('[Auth] Google redirect sign-in successful', { uid: user.uid, email: user.email });
      return user;
    } catch (err) {
      dev.error('[Auth] Google redirect result failed', err);
      if ((err as Error & { code?: string }).code === 'app/beta-access-denied') {
        await getServices().auth.signOut().catch(() => {});
      }
      throw err;
    }
  }

  async signInWithEmail(email: string, password: string): Promise<BLUser> {
    // assertBetaAccess(email);
    try {
      const authUser = await getServices().auth.signInWithEmail(email, password);
      const user = await this.registerUserIfNeeded(authUser);
      dev.log('[Auth] Email sign-in successful', { uid: user.uid, email: user.email });
      return user;
    } catch (err) {
      dev.error('[Auth] Email sign-in failed', err);
      throw err;
    }
  }

  async registerWithEmail(email: string, password: string): Promise<BLUser> {
    // assertBetaAccess(email);
    try {
      const authUser = await getServices().auth.registerWithEmail(email, password);
      const user = await this.registerUserIfNeeded(authUser);
      dev.log('[Auth] Email registration successful', { uid: user.uid, email: user.email });
      return user;
    } catch (err) {
      dev.error('[Auth] Email registration failed', err);
      throw err;
    }
  }

  async signOut(): Promise<void> {
    try {
      await getServices().auth.signOut();
      dev.log('[Auth] Sign-out successful');
    } catch (err) {
      dev.error('[Auth] Sign-out failed', err);
      throw err;
    }
  }

  async deleteAccount(params: DeleteAccountParams): Promise<void> {
    const auth = getServices().auth;
    if (!auth.currentUser) throw new Error('No authenticated user');

    try {
      if (params.provider === 'google') {
        await auth.reauthenticateGoogleAndDelete();
      } else if (params.provider === 'apple') {
        // Apple Sign In is iOS-native only; no web reauth path exists.
        throw new Error('Apple Sign In delete is not supported on web');
      } else {
        await auth.reauthenticateEmailAndDelete(params.email, params.password);
      }
      dev.log('[Auth] Account deleted successfully');
    } catch (err) {
      dev.error('[Auth] Account deletion failed', err);
      throw err;
    }
  }

  async fetchUser(): Promise<BLUser | null> {
    const firebaseUser = getServices().auth.currentUser;
    if (!firebaseUser) return null;

    try {
      const data = await getServices().userData.fetchUserDoc(firebaseUser.uid);
      if (!data) return null;
      const user = blUserFromFirestore(data);
      const now = new Date();
      const timezoneOffsetFromUtc = now.getTimezoneOffset() * -60;
      user.lastSeenAt = now;
      user.webVersion = WEB_VERSION;
      user.webBuildNumber = WEB_BUILD_NUMBER;
      user.locale = navigator.language;
      user.timezoneOffsetFromUtc = timezoneOffsetFromUtc;
      // Fire-and-forget: analytics write must not block or fail user hydration
      getServices().userData.mergeUserDoc(firebaseUser.uid, {
        lastSeenAt: Timestamp.fromDate(now),
        webVersion: WEB_VERSION,
        webBuildNumber: WEB_BUILD_NUMBER,
        locale: navigator.language,
        timezoneOffsetFromUtc,
      }).catch((e) => dev.warn('[auth] lastSeenAt write failed', e));
      return user;
    } catch {
      return null;
    }
  }

  async recordOnboardedAt(uid: string): Promise<void> {
    await getServices().userData.mergeUserDoc(uid, { onboardedAt: Timestamp.now() });
  }

  async updateUser(user: BLUser): Promise<void> {
    await getServices().userData.writeUserDoc(user.uid, blUserToFirestore(user));
  }

  async updateWebFcmToken(uid: string, token: string | null): Promise<void> {
    await getServices().userData.writeUserDoc(uid, { webFcmToken: token });
  }

  listenToUser(uid: string, onUpdate: (user: BLUser) => void): () => void {
    return getServices().userData.listenToUserDoc(uid, (data) => {
      onUpdate(blUserFromFirestore(data));
    });
  }

  private async registerUserIfNeeded(firebaseUser: AuthUser): Promise<BLUser> {
    const data = await getServices().userData.fetchUserDoc(firebaseUser.uid);

    if (data) {
      const user = blUserFromFirestore(data);
      user.lastLoginAt = new Date();
      user.termsAndPrivacyAgreedAt = [...(user.termsAndPrivacyAgreedAt ?? []), new Date()];
      user.locale = navigator.language;
      user.timezoneOffsetFromUtc = new Date().getTimezoneOffset() * -60;
      await this.updateUser(user);
      return user;
    }

    const providerData = firebaseUser.providerData[0];
    const loginProvider = loginProviderFromFirebaseId(providerData?.providerId ?? 'password') ?? LoginProvider.email;
    const displayName = firebaseUser.displayName?.split(' ')[0] ?? null;
    const email = firebaseUser.email ?? providerData?.email ?? null;

    let breathworkNameDisplay = defaultUserSettings().breathworkNameDisplay;
    try {
      const rc = getServices().remoteConfig;
      await rc.fetchAndActivate();
      breathworkNameDisplay = rc.getDefaultBreathworkNameDisplay();
    } catch {
      // keep default
    }

    const now = new Date();
    const newUser: BLUser = {
      uid: firebaseUser.uid,
      accessLevel: AccessLevel.free,
      connectedSignInProviders: [loginProvider],
      createdAt: now,
      displayName,
      email,
      fcmToken: null,
      webFcmToken: null,
      lastLoginAt: now,
      lastSeenAt: now,
      photoRef: null,
      reminderTimeUtcMinutesSinceMidnight: null,
      termsAndPrivacyAgreedAt: [now],
      // Seconds east of UTC — matches Flutter's DateTimeExtensions.getTimeZoneOffsetInSeconds()
      timezoneOffsetFromUtc: new Date().getTimezoneOffset() * -60,
      appVersion: null,
      appBuildNumber: null,
      webVersion: WEB_VERSION,
      webBuildNumber: WEB_BUILD_NUMBER,
      signupPlatform: 'web',
      locale: navigator.language,
      settings: { ...defaultUserSettings(), breathworkNameDisplay },
      breathworkMetrics: null,
      referredBy: captureReferralOnSignup(),
      onboardedAt: null,
      trialStartedAt: null,
    };

    if (newUser.referredBy) {
      logEvent(AnalyticsEvent.referralSignUp, { [AnalyticsParam.referrerUid]: newUser.referredBy });
    }

    // signup_platform is write-once: included here at creation but omitted from
    // blUserToFirestore so a later cross-platform login never overwrites it.
    await getServices().userData.writeUserDoc(newUser.uid, {
      ...blUserToFirestore(newUser),
      signupPlatform: 'web',
    });
    logEvent(AnalyticsEvent.experimentAssigned, {
      [AnalyticsParam.experimentName]: 'default_breathwork_name_display',
      [AnalyticsParam.experimentVariant]: breathworkNameDisplay,
    });
    return newUser;
  }
}
